import React, { forwardRef, useRef, useEffect } from 'react';
import MuxPlayer, { MuxPlayerProps } from '@mux/mux-player-react';
import MuxPlayerElement, { Tokens } from '@mux/mux-player';

const muxUrlToIdAndTokenRegex = /https:\/\/stream\.mux\.com\/(.*)\.m3u8\?token=(.*)/;

// VideoPlayerProps extends MuxPlayerProps with some of the defaults used to add additional functionality.  Information
// about MuxPlayerProps can be found here:
// https://github.com/muxinc/elements/blob/HEAD/packages/mux-player-react/REFERENCE.md
export interface VideoPlayerProps extends MuxPlayerProps {
  // muxPlaybackUrl is the URL of the video to play.  If specified, muxPlaybackId and muxSignedToken will be ignored.
  // This is a mux specific URL in the format of: https://stream.mux.com/{PLAYBACK_ID}.m3u8?token={SIGNED_TOKEN}
  muxSignedPlaybackUrl?: string;

  // muxPlaybackId is the ID of the video to play.  This is a mux specific ID.
  muxPlaybackId?: string;

  // muxSignedToken is a signed token that allows playback of a specific video.  If present, it will be used along with
  // the muxPlaybackId to play the video.
  muxSignedToken?: string;

  // muted is a flag to indicate if the video should be muted.  Defaults to true.
  muted?: boolean;

  // emitVideoWatchedMetrics is a function that is called
  // when the video player is unmounted, and contains relevant metrics
  // whether user has watched the video, and how much of the video was watched.
  emitVideoWatchedMetrics?: (properties: Record<string, unknown>) => void;

  // onAutoplayResult is called after an autoplay attempt when autoPlay is set.
  // The player is always muted before attempting autoplay to maximise browser compatibility.
  // succeeded is true if playback started, false if the browser blocked it.
  onAutoplayResult?: (succeeded: boolean) => void;
}

// VideoPlayer is a thin wrapper around MuxPlayer.  It's here to provide a consistent interface for our components to
// use and may eventually contain extra functionality and styling.  The props are extended from MuxPlayerProps.
// primaryColor and accentColor (part of MuxPlayerProps) use the CSS variables by default but can be overridden.
export const VideoPlayer = forwardRef<MuxPlayerElement, VideoPlayerProps>(
  (
    {
      muxPlaybackId,
      muxSignedToken,
      muxSignedPlaybackUrl,
      muted = true,
      primaryColor = 'var(--on-primary-color)',
      accentColor = 'var(--primary-color)',
      emitVideoWatchedMetrics = () => {},
      autoPlay,
      onAutoplayResult,
      ...props
    },
    ref
  ) => {
    const hasVideoWatched = useRef(false);
    const videoDurationInSeconds = useRef(0);
    const trackingWatchedTimeInSeconds = useRef(0); // in seconds
    const internalRef = useRef<MuxPlayerElement | null>(null);
    const onAutoplayResultRef = useRef(onAutoplayResult);
    onAutoplayResultRef.current = onAutoplayResult;
    const autoplaySucceeded = useRef<boolean | null>(null);

    // Merge the internal ref (needed for autoplay) with the forwarded ref.
    const setRef = (el: MuxPlayerElement | null) => {
      internalRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    };

    useEffect(() => {
      const handleOnUnMount = () => {
          // Only emit the metrics if the parent provided the call back.
        const properties: Record<string, unknown> = {
          video_ended: hasVideoWatched.current,
          // If 0, it means user has not watched the video
          video_watched_percentage: trackingWatchedTimeInSeconds.current > 0
            ? Math.min(1, (trackingWatchedTimeInSeconds.current / videoDurationInSeconds.current)) * 100
            : 0,
        };
        if (autoPlay) {
          properties.auto_play_set = true;
          properties.auto_play_succeeded = autoplaySucceeded.current ?? false;
        }
        emitVideoWatchedMetrics(properties);
      };
      return () => {
        handleOnUnMount();
      };
    }, [muxPlaybackId]);

    useEffect(() => {
      if (!autoPlay || !internalRef.current) return;

      const player = internalRef.current;
      let cancelled = false;

      const attemptAutoplay = async () => {
        if (cancelled) return;
        player.muted = true;
        try {
          await player.play();
          if (!cancelled) {
            autoplaySucceeded.current = true;
            onAutoplayResultRef.current?.(true);
          }
        } catch {
          if (!cancelled) {
            autoplaySucceeded.current = false;
            onAutoplayResultRef.current?.(false);
          }
        }
      };

      // If the player already has enough data, attempt immediately; otherwise wait for canplay.
      if (player.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        attemptAutoplay();
      } else {
        player.addEventListener('canplay', attemptAutoplay, { once: true });
      }

      return () => {
        cancelled = true;
        player.removeEventListener('canplay', attemptAutoplay);
      };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally runs once on mount

    const handleLoadedMetadata = (event: Event) => {
      // Only emit the metrics if the parent provided the call back.
      const player = event.currentTarget as MuxPlayerElement;
      videoDurationInSeconds.current = player.duration;
      
    };

    const handleTimeUpdate = (event: Event) => {
      // Only emit the metrics if the parent provided the call back.
      const player = event.currentTarget as MuxPlayerElement;
      trackingWatchedTimeInSeconds.current = Math.max(player.currentTime, trackingWatchedTimeInSeconds.current);

    };
  
    const handleEnded = (event: Event) => {
      // Only emit the metrics if the parent provided the call back.
      const player = event.currentTarget as MuxPlayerElement;
      hasVideoWatched.current = true;
      trackingWatchedTimeInSeconds.current = player.duration;
    }; 

    if (muxSignedPlaybackUrl) {
      const match = muxSignedPlaybackUrl.match(muxUrlToIdAndTokenRegex);
      if (match) {
        muxPlaybackId = match[1];
        muxSignedToken = match[2];
      }
    }

    // Add the signed token to the tokens object if it exists.  This is used by MuxPlayer for video playback and for
    // retrieving captions. We used to use the playback URL from that the server creates, but that doesn't
    // work for captions and the player was throwing errors. So we updated it to take the playbackId and token instead.
    const tokens: Tokens = {};
    if (muxSignedToken) {
      tokens.playback = muxSignedToken;
    }

    // TODO: This (poster={props.poster || ''} is a temporary solution to get around MuxPlayer showing a broken image for
    //  the poster image.  We should use a generated thumbnail from the video instead.
    return (
      <MuxPlayer
        ref={setRef}
        streamType="on-demand"
        tokens={tokens}
        playbackId={muxPlaybackId}
        muted={muted}
        primaryColor={primaryColor}
        accentColor={accentColor}
        poster={props.poster || ''}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        {...props}
      />
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';
